import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { pauseDosingSchedule, type DosingSchedule } from '../../services/dosingScheduleService';

export interface DosingPauseFormProps {
  aquariumId: string;
  schedule: DosingSchedule;
  onSuccess: (schedule: DosingSchedule) => void;
  onCancel: () => void;
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Pausieren": pause a plan now, optionally until a date (auto-resume). */
export function DosingPauseForm({ aquariumId, schedule, onSuccess, onCancel }: DosingPauseFormProps) {
  const { t } = useTranslation();
  const [until, setUntil] = useState('');
  const [untilInvalid, setUntilInvalid] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSaveFailed(false);
    const invalid = until.length > 0 && until <= todayLocal();
    setUntilInvalid(invalid);
    if (invalid) return;
    setIsSubmitting(true);
    try {
      onSuccess(await pauseDosingSchedule(aquariumId, schedule.id, until.length > 0 ? until : null));
    } catch {
      setSaveFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="4">
        {saveFailed && (
          <Alert tone="danger" data-testid="dosing-pause-error">
            {t('dosing.pause.saveError')}
          </Alert>
        )}
        <Input
          id="dosing-pause-until"
          type="date"
          label={t('dosing.pause.until')}
          value={until}
          helperText={schedule.dosing_pump ? t('dosing.pause.helpPump') : t('dosing.pause.help')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setUntil(e.target.value)}
          error={untilInvalid ? t('dosing.pause.untilError') : undefined}
        />
        <Inline gap="3" justify="end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('dosing.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {t('dosing.pause.submit')}
          </Button>
        </Inline>
      </Stack>
    </form>
  );
}
